import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Check, Trash2, Loader2 } from "lucide-react";

export default function Drafts() {
  const nav = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/products", { params: { status: "draft", limit: 200 } });
    setDrafts(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const patch = (id, patch) => setDrafts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const save = async (p) => {
    setBusy(p.id);
    try {
      await api.put(`/products/${p.id}`, {
        ...p,
        selling_price: Number(p.selling_price) || 0,
        mrp: p.mrp === "" || p.mrp == null ? null : Number(p.mrp),
        stock_quantity: p.stock_quantity === "" || p.stock_quantity == null ? null : parseInt(p.stock_quantity, 10),
      });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(null); }
  };

  const publish = async (p) => {
    setBusy(p.id);
    try {
      await api.put(`/products/${p.id}`, {
        ...p,
        status: "published",
        selling_price: Number(p.selling_price) || 0,
        mrp: p.mrp === "" || p.mrp == null ? null : Number(p.mrp),
        stock_quantity: p.stock_quantity === "" || p.stock_quantity == null ? null : parseInt(p.stock_quantity, 10),
      });
      toast.success("Published");
      setDrafts((prev) => prev.filter((x) => x.id !== p.id));
    } catch { toast.error("Publish failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/products/${id}`);
    setDrafts((prev) => prev.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <AppFrame>
      <TopBar title="Drafts" subtitle={`${drafts.length} pending`} />
      <div className="px-5 mt-4 space-y-3">
        {loading ? (
          <div className="py-10 grid place-items-center" style={{ color: "var(--ink-3)" }}><Loader2 className="animate-spin" /></div>
        ) : drafts.length === 0 ? (
          <div className="rounded-2xl py-14 text-center" style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}>
            <div className="font-display-italic text-xl">no drafts</div>
            <p className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>Bulk upload some images to get started.</p>
          </div>
        ) : drafts.map((p) => (
          <div
            key={p.id}
            data-testid={`draft-${p.id}`}
            className="rounded-2xl p-3 space-y-2"
            style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
          >
            <div className="flex gap-3">
              <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0" style={{ background: "#F1E8D8" }}>
                {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  data-testid={`draft-name-${p.id}`}
                  value={p.name || ""}
                  onChange={(e) => patch(p.id, { name: e.target.value })}
                  placeholder="Name"
                  className="w-full h-9 rounded-lg px-2 text-sm outline-none"
                  style={{ border: "1px solid rgba(27,25,23,0.12)" }}
                />
                <div className="grid grid-cols-3 gap-1.5">
                  <input inputMode="decimal" placeholder="Sale ₹" value={p.selling_price ?? ""} onChange={(e) => patch(p.id, { selling_price: e.target.value })} data-testid={`draft-price-${p.id}`} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                  <input inputMode="decimal" placeholder="MRP" value={p.mrp ?? ""} onChange={(e) => patch(p.id, { mrp: e.target.value })} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                  <input inputMode="numeric" placeholder="Stock" value={p.stock_quantity ?? ""} onChange={(e) => patch(p.id, { stock_quantity: e.target.value })} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <input placeholder="Size" value={p.size ?? ""} onChange={(e) => patch(p.id, { size: e.target.value })} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                  <input placeholder="Weight" value={p.weight ?? ""} onChange={(e) => patch(p.id, { weight: e.target.value })} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                  <input placeholder="Color" value={p.color ?? ""} onChange={(e) => patch(p.id, { color: e.target.value })} className="h-9 rounded-lg px-2 text-xs outline-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
                </div>
                <textarea placeholder="Description" value={p.description ?? ""} onChange={(e) => patch(p.id, { description: e.target.value })} rows={2} className="w-full rounded-lg px-2 py-1.5 text-xs outline-none resize-none" style={{ border: "1px solid rgba(27,25,23,0.12)" }} />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid={`draft-save-${p.id}`}
                onClick={() => save(p)}
                disabled={busy === p.id}
                className="flex-1 h-10 rounded-full text-[11px] tracking-editorial-sm uppercase press disabled:opacity-60"
                style={{ border: "1px solid rgba(27,25,23,0.15)", color: "var(--ink)" }}
              >Save</button>
              <button
                data-testid={`draft-publish-${p.id}`}
                onClick={() => publish(p)}
                disabled={busy === p.id}
                className="flex-1 h-10 rounded-full text-[11px] tracking-editorial-sm uppercase press disabled:opacity-60 flex items-center justify-center gap-1"
                style={{ background: "var(--ink)", color: "var(--bg)" }}
              >
                <Check size={12} /> Publish
              </button>
              <button
                data-testid={`draft-del-${p.id}`}
                onClick={() => del(p.id)}
                className="w-10 h-10 rounded-full grid place-items-center press"
                style={{ border: "1px solid rgba(27,25,23,0.15)", color: "var(--accent)" }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <BottomNav />
    </AppFrame>
  );
}
